import { useCallback, useState, type ReactElement } from "react";
import { Shield, UserMinus } from "lucide-react";
import { space } from "@tokens/index";
import { Button, Text } from "@primitives/index";
import { AdminSection, DataRow, EmptyLine, MutationError } from "../admin-ui";
import { useAddManagementGroupAdmin, useRemoveManagementGroupAdmin } from "../management-groups";
import type { ManagementGroup } from "../management-groups";
import { useUserSearch } from "../queues";
import type { UserSearchResult } from "../queues";
import { UserSearchPicker } from "./UserSearchPicker";
import { resolveUserDisplayName } from "./userDisplayName";

export function AdminsSection({ group }: { readonly group: ManagementGroup }): ReactElement {
    const add = useAddManagementGroupAdmin();
    const remove = useRemoveManagementGroupAdmin();
    const [searchTerm, setSearchTerm] = useState("");
    const userSearch = useUserSearch({ q: searchTerm });

    const handleAdd = useCallback(
        (user: UserSearchResult) => {
            add.mutate(
                { id: group.id, userId: user.id, displayName: resolveUserDisplayName(user) },
                { onSuccess: () => setSearchTerm("") },
            );
        },
        [add, group.id],
    );

    const handleRemove = useCallback(
        (userId: string) => {
            remove.mutate({ id: group.id, userId });
        },
        [remove, group.id],
    );

    const results = userSearch.data?.results ?? [];
    const alreadyAdmin = new Set(group.admins.map((a) => a.userId));

    return (
        <AdminSection title="Admins" icon={Shield} count={group.admins.length}>
            {group.admins.length === 0 ? (
                <EmptyLine>
                    No admins yet. Add a user below to give them administrative rights for this
                    group.
                </EmptyLine>
            ) : (
                <div role="list" aria-label="Admins" style={{ display: "grid", gap: space[2] }}>
                    {group.admins.map((a) => (
                        <div role="listitem" key={a.userId}>
                            <DataRow
                                title={a.displayName}
                                meta={
                                    <Text role="mono" size="xs" tone="muted" truncate>
                                        {a.userId}
                                    </Text>
                                }
                                trailing={
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemove(a.userId)}
                                        disabled={remove.isPending}
                                        aria-label={`Remove ${a.displayName}`}
                                    >
                                        <UserMinus
                                            size={14}
                                            strokeWidth={2.25}
                                            aria-hidden="true"
                                        />
                                        Remove
                                    </Button>
                                }
                            />
                        </div>
                    ))}
                </div>
            )}

            <UserSearchPicker
                label="Search HR users to add as admins"
                placeholder="Name, email, or user id"
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
                results={results}
                isFetching={userSearch.isFetching}
                excludedIds={alreadyAdmin}
                onAdd={handleAdd}
                isAdding={add.isPending}
                addLabel="Add admin"
                excludedLabel="Already admin"
            />

            <MutationError error={add.error ?? remove.error} />
        </AdminSection>
    );
}
